import { useEffect, useMemo, useRef } from 'react';
import {
  channelScopeFor,
  channelScopeKey,
  type ChannelScopeResult,
  type OrderablePackage,
  type ShippingChannel,
} from '@mypoz/shared';

import { useOrderCatalog, type CatalogFetchStatus } from './catalogContext.js';

export interface ScopedChannels {
  /** Whether the selection can be looked up, and why not when it cannot. */
  readonly result: ChannelScopeResult;
  /** `idle` only when there is nothing to look up (`result.kind !== 'ok'`). */
  readonly status: CatalogFetchStatus;
  /** The channels on offer for the selection. Empty until `status` is `ready`. */
  readonly channels: readonly ShippingChannel[];
  readonly retry: () => void;
}

/**
 * The channels the selected packages can travel on (MYP-87), fetched as soon as the selection
 * settles on one warehouse and goods type — on the packages step already, so the list is usually
 * there by the time the customer reaches the channel step, and so a channel chosen earlier can be
 * checked against the new selection wherever the customer is.
 */
export function useScopedChannels(selected: readonly OrderablePackage[]): ScopedChannels {
  const { loadChannels, channelsFor, channelsStatus } = useOrderCatalog();
  const result = useMemo(() => channelScopeFor(selected), [selected]);
  const scope = result.kind === 'ok' ? result.scope : undefined;
  const key = scope ? channelScopeKey(scope) : undefined;

  // The latest scope, for the effect below: it runs per key, not per package-id list, because the
  // list depends on the key alone (see `channelScopeKey`).
  const latest = useRef(scope);
  latest.current = scope;

  useEffect(() => {
    if (key !== undefined && latest.current) loadChannels(latest.current);
  }, [key, loadChannels]);

  const fetched = scope ? channelsStatus(scope) : 'idle';

  // Stable per key and cached rows, so callers can depend on it without re-running every render.
  const channels = useMemo(
    () => (key !== undefined && latest.current ? channelsFor(latest.current) : []),
    [key, channelsFor],
  );

  return {
    result,
    // Between the first render and the effect's first fetch, a scope that exists is loading.
    status: scope && fetched === 'idle' ? 'loading' : fetched,
    channels,
    retry: () => {
      if (latest.current) loadChannels(latest.current);
    },
  };
}
